import axios, { AxiosRequestConfig, CancelTokenSource } from "axios";
import { storageService } from "./storageService";

const API_URL = process.env.REACT_APP_V5_API_URL ?? "";

export type UploadStatus = "pending" | "uploading" | "sent" | "failed";

export interface UploadTask {
  id: string; // tempMsgId
  chatId: string;
  formData: FormData;
  status: UploadStatus;
  progress: number; // 0–100
  cancelSource: CancelTokenSource | null;
  retryCount: number;
  error?: string;
}

type Listener = (task: UploadTask) => void;

class UploadManager {
  private tasks = new Map<string, UploadTask>();
  private listeners = new Map<string, Set<Listener>>();
  private globalListeners = new Set<Listener>();
  private maxRetries = 3;

  /** Subscribe to updates for a specific upload task */
  subscribe(taskId: string, listener: Listener): () => void {
    if (!this.listeners.has(taskId)) {
      this.listeners.set(taskId, new Set());
    }
    this.listeners.get(taskId)!.add(listener);
    // Emit current state immediately
    const task = this.tasks.get(taskId);
    if (task) listener(task);
    return () => {
      this.listeners.get(taskId)?.delete(listener);
    };
  }

  /** Subscribe to ALL upload updates (for global UI) */
  subscribeAll(listener: Listener): () => void {
    this.globalListeners.add(listener);
    return () => {
      this.globalListeners.delete(listener);
    };
  }

  private notify(task: UploadTask) {
    this.listeners.get(task.id)?.forEach((fn) => fn(task));
    this.globalListeners.forEach((fn) => fn(task));
  }

  /** Get current state of an upload */
  getTask(taskId: string): UploadTask | undefined {
    return this.tasks.get(taskId);
  }

  /** Start an upload. Returns a promise that resolves with the API response. */
  async upload(
    taskId: string,
    chatId: string,
    formData: FormData,
  ): Promise<any> {
    const cancelSource = axios.CancelToken.source();

    const task: UploadTask = {
      id: taskId,
      chatId,
      formData,
      status: "uploading",
      progress: 0,
      cancelSource,
      retryCount: 0,
    };

    this.tasks.set(taskId, task);
    this.notify(task);

    return this.executeUpload(task);
  }

  private async executeUpload(task: UploadTask): Promise<any> {
    const token = storageService.getAccessToken();
    const cancelSource = axios.CancelToken.source();
    task.cancelSource = cancelSource;
    task.status = "uploading";
    this.notify(task);

    const config: AxiosRequestConfig = {
      baseURL: API_URL,
      url: "/chat/send-message-media",
      method: "POST",
      data: task.formData,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      cancelToken: cancelSource.token,
      onUploadProgress: (event) => {
        if (event.total) {
          task.progress = Math.round((event.loaded / event.total) * 100);
          this.notify(task);
        }
      },
    };

    try {
      const response = await axios(config);
      task.status = "sent";
      task.progress = 100;
      task.cancelSource = null;
      this.notify(task);
      return response.data;
    } catch (err: any) {
      if (axios.isCancel(err)) {
        // User cancelled — don't retry. Return a sentinel instead of throwing
        // so callers that don't await (e.g. retry button onClick) don't produce
        // unhandled Promise rejections shown by the dev overlay.
        task.status = "failed";
        task.error = "Upload cancelled";
        task.cancelSource = null;
        this.notify(task);
        return { cancelled: true };
      }

      task.retryCount++;
      if (task.retryCount <= this.maxRetries) {
        // Auto-retry with backoff
        const delay = Math.min(1000 * Math.pow(2, task.retryCount - 1), 8000);
        await new Promise((r) => setTimeout(r, delay));
        return this.executeUpload(task);
      }

      task.status = "failed";
      task.error = err?.response?.data?.message || err.message || "Upload failed";
      task.cancelSource = null;
      this.notify(task);
      throw err;
    }
  }

  /** Manual retry for a failed upload */
  async retry(taskId: string): Promise<any> {
    const task = this.tasks.get(taskId);
    if (!task || task.status !== "failed") return;

    task.retryCount = 0;
    task.progress = 0;
    task.error = undefined;
    try {
      return await this.executeUpload(task);
    } catch {
      // task state already reflects the failure — swallow so callers that
      // don't await retry() don't produce unhandled Promise rejections.
    }
  }

  /** Cancel an in-progress upload */
  cancel(taskId: string) {
    const task = this.tasks.get(taskId);
    if (task?.cancelSource) {
      task.cancelSource.cancel("Upload cancelled by user");
    }
  }

  /** Remove a task from tracking */
  remove(taskId: string) {
    this.cancel(taskId);
    this.tasks.delete(taskId);
    this.listeners.delete(taskId);
  }
}

// Singleton — survives component unmounts
const uploadManager = new UploadManager();
export default uploadManager;
